import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import {
  getMessagesBefore,
  getMessagesAfter,
  sendMessage,
  deleteConversation,
  deleteMessage,
} from '@/services/v2/conversations';
import { Conversation, Message } from '@/services/v2/types';
import ChatMessage from './ChatMessage';

type Props = {
  conversation: Conversation | null;
  currentUserId: string;
  onDeleted?: (conversationId: number) => void;
};

const PAGE_SIZE = 20; // messages fetched per history page

/** A 404 on a conversation means it no longer exists for this user — deleted by either side. */
const isConversationGone = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 404;

export default function ChatPanel({ conversation, currentUserId, onDeleted }: Props) {
  const [messages, setMessages] = useState<Message[]>([]); // descending: index 0 = newest
  const [hasMoreOlder, setHasMoreOlder] = useState(true);  // more history below oldestIdRef
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const [confirmingDeleteMessageId, setConfirmingDeleteMessageId] = useState<number | null>(null);
  const [deletingMessage, setDeletingMessage] = useState(false);
  const [deleteMessageError, setDeleteMessageError] = useState(false);

  const latestIdRef = useRef<number | null>(null); // true newest id ever seen, drives polling; null = initial page not loaded yet
  const oldestIdRef = useRef<number | null>(null); // oldest id currently loaded
  const scrollRef = useRef<HTMLDivElement>(null);
  const olderSentinelRef = useRef<HTMLDivElement>(null);

  // Initial load on conversation change
  useEffect(() => {
    if (!conversation) return;
    setMessages([]);
    setHasMoreOlder(true);
    latestIdRef.current = null;
    oldestIdRef.current = null;

    getMessagesBefore(conversation.id, null, PAGE_SIZE)
      .then((initial) => {
        setMessages(initial);
        // Always set, even when initial is empty — 0 marks "loaded, nothing newer yet"
        // and lets polling/refetch fetch anything sent after this baseline (id > 0).
        latestIdRef.current = initial.length > 0 ? initial[0].id : 0; // index 0 = newest
        if (initial.length > 0) {
          oldestIdRef.current = initial[initial.length - 1].id;
        }
        setHasMoreOlder(initial.length === PAGE_SIZE);
      })
      .catch((error) => {
        // Stale link to a chat the other participant already deleted.
        if (isConversationGone(error)) {
          onDeleted?.(conversation.id);
          return;
        }
        throw error;
      });
  }, [conversation?.id]);

  // Never carry a pending "delete this chat?" prompt over to another conversation.
  useEffect(() => {
    setConfirmingDelete(false);
    setDeleteError(false);
    setConfirmingDeleteMessageId(null);
    setDeleteMessageError(false);
  }, [conversation?.id]);

  // Poll for new messages every 5 seconds — sole writer of confirmed messages into state
  const { refetch: refetchMessages } = useQuery({
    queryKey: ['messages-poll', conversation?.id],
    queryFn: async () => {
      if (!conversation || latestIdRef.current === null) return [];
      let fresh: Message[];
      try {
        fresh = await getMessagesAfter(conversation.id, latestIdRef.current, 50);
      } catch (error) {
        // The other participant deleted the chat while we had it open.
        if (isConversationGone(error)) {
          onDeleted?.(conversation.id);
          return [];
        }
        throw error;
      }
      if (fresh.length > 0) {
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const incoming    = fresh.filter((m) => !existingIds.has(m.id));
          if (incoming.length === 0) return prev;
          latestIdRef.current = incoming[0].id; // incoming is descending, [0] = newest
          return [...incoming, ...prev];
        });
      }
      return fresh;
    },
    refetchInterval: 5000,
    enabled: !!conversation && latestIdRef.current !== null,
  });

  // Load older messages when the top sentinel scrolls into view (DOM-last = visual top in column-reverse)
  const loadOlder = useCallback(async () => {
    if (!conversation || loadingOlder || !hasMoreOlder || oldestIdRef.current === null) return;
    setLoadingOlder(true);
    const older = await getMessagesBefore(conversation.id, oldestIdRef.current, PAGE_SIZE);
    setMessages((prev) => {
      const existingIds = new Set(prev.map((m) => m.id));
      const incoming    = older.filter((m) => !existingIds.has(m.id));
      if (incoming.length === 0) return prev;
      oldestIdRef.current = incoming[incoming.length - 1].id;
      return [...prev, ...incoming];
    });
    setHasMoreOlder(older.length === PAGE_SIZE);
    setLoadingOlder(false);
  }, [conversation, loadingOlder, hasMoreOlder]);

  useEffect(() => {
    const el = olderSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadOlder(); },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadOlder]);

  const handleSend = async () => {
    if (!conversation || !body.trim() || sending) return;
    const text = body.trim();
    setBody('');
    setSending(true);
    try {
      await sendMessage(conversation.id, text);
      refetchMessages(); // pull the confirmed message in immediately instead of waiting up to 5s
    } catch {
      setBody(text); // restore the draft so it isn't lost
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!conversation || deleting) return;
    setDeleting(true);
    setDeleteError(false);
    try {
      await deleteConversation(conversation.id);
      setConfirmingDelete(false);
      onDeleted?.(conversation.id);
    } catch {
      setDeleteError(true);
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmDeleteMessage = async () => {
    if (confirmingDeleteMessageId === null || deletingMessage) return;
    const messageId = confirmingDeleteMessageId;
    setDeletingMessage(true);
    setDeleteMessageError(false);
    try {
      await deleteMessage(messageId);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      setConfirmingDeleteMessageId(null);
    } catch {
      setDeleteMessageError(true);
    } finally {
      setDeletingMessage(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!conversation) {
    return <Empty>Select a conversation to start chatting.</Empty>;
  }

  return (
    <Container>
      <ChatHeader>
        <HeaderName>{conversation.other_user.username}</HeaderName>
        <DeleteChatButton
          type='button'
          onClick={() => setConfirmingDelete(true)}
          disabled={confirmingDelete || deleting}
        >
          Delete chat
        </DeleteChatButton>
      </ChatHeader>

      {confirmingDelete && (
        <ConfirmBar role='alertdialog' aria-label='Delete chat'>
          <ConfirmText>
            Delete this chat? It&apos;s removed from your Messages list.{' '}
            <strong>{conversation.other_user.username}</strong> will still see it unless they
            also delete it.
          </ConfirmText>
          {deleteError && (
            <ConfirmError>Couldn&apos;t delete the chat. Please try again.</ConfirmError>
          )}
          <ConfirmActions>
            <CancelButton
              type='button'
              onClick={() => {
                setConfirmingDelete(false);
                setDeleteError(false);
              }}
              disabled={deleting}
            >
              Cancel
            </CancelButton>
            <ConfirmDeleteButton type='button' onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete for me'}
            </ConfirmDeleteButton>
          </ConfirmActions>
        </ConfirmBar>
      )}

      {confirmingDeleteMessageId !== null && (
        <ConfirmBar role='alertdialog' aria-label='Delete message'>
          <ConfirmText>
            Delete this message? It&apos;s removed from your view only.{' '}
            <strong>{conversation.other_user.username}</strong> will still see it unless they
            also delete it.
          </ConfirmText>
          {deleteMessageError && (
            <ConfirmError>Couldn&apos;t delete the message. Please try again.</ConfirmError>
          )}
          <ConfirmActions>
            <CancelButton
              type='button'
              onClick={() => {
                setConfirmingDeleteMessageId(null);
                setDeleteMessageError(false);
              }}
              disabled={deletingMessage}
            >
              Cancel
            </CancelButton>
            <ConfirmDeleteButton
              type='button'
              onClick={handleConfirmDeleteMessage}
              disabled={deletingMessage}
            >
              {deletingMessage ? 'Deleting…' : 'Delete for me'}
            </ConfirmDeleteButton>
          </ConfirmActions>
        </ConfirmBar>
      )}

      <ScrollArea ref={scrollRef}>
        {messages.map((msg) => (
          <ChatMessage
            key={msg.id}
            message={msg}
            currentUserId={currentUserId}
            onDelete={setConfirmingDeleteMessageId}
          />
        ))}

        {/* DOM-last → visual top due to column-reverse → triggers loadOlder */}
        <Sentinel ref={olderSentinelRef}>
          {loadingOlder && <LoadingText>Loading…</LoadingText>}
          {!hasMoreOlder && messages.length > 0 && <LoadingText>No more messages</LoadingText>}
        </Sentinel>
      </ScrollArea>

      <Form onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder='Type a message…'
          rows={1}
          maxLength={4096}
        />
        <SendButton type='submit' disabled={!body.trim() || sending}>Send</SendButton>
      </Form>
    </Container>
  );
}

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 700;
  font-size: 16px;
  padding: 16px;
  border-bottom: 1px solid #eee;
`;

const HeaderName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DeleteChatButton = styled.button`
  margin-left: auto;
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #c0392b;
  cursor: pointer;
  &:disabled { color: #ccc; cursor: not-allowed; }
  &:hover:not(:disabled) { text-decoration: underline; }
`;

const ConfirmBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  background: #fdf3f2;
  border-bottom: 1px solid #f2d6d3;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.75);
`;

const ConfirmError = styled.p`
  margin: 0;
  font-size: 13px;
  color: #c0392b;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 8px;
`;

const CancelButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
  &:disabled { cursor: not-allowed; opacity: 0.6; }
`;

const ConfirmDeleteButton = styled.button`
  background: #c0392b;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { background: #ccc; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #a33227; }
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: scroll;
  display: flex;
  flex-direction: column-reverse;
  padding: 8px 0;
`;

const Sentinel = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px;
`;

const LoadingText = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.4);
`;

const Empty = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.4);
  font-size: 14px;
`;

const Form = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #eee;
`;

const Textarea = styled.textarea`
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  resize: none;
  font-family: inherit;
  line-height: 1.4;
  &:focus { outline: none; border-color: #2d8c7a; }
`;

const SendButton = styled.button`
  background: #2d8c7a;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { background: #ccc; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #1f6b5e; }
`;
